// Business side: one client's billing. Recurring schedules (create, edit,
// pause, resume, end) and one-time invoices saved as a draft or sent now.
// A schedule creates a normal numbered invoice on each run date.
import React, { useCallback, useEffect, useState } from 'react';
import clientPortalAPI from './clientPortalApi';
import { money, day } from './format';
import { formatApiError } from '../../utils/errorMessage';

export const FREQUENCIES = [
  ['weekly', 'Weekly'], ['biweekly', 'Every 2 weeks'], ['monthly', 'Monthly'],
  ['quarterly', 'Every 3 months'], ['semi_annual', 'Every 6 months'], ['yearly', 'Yearly'],
];
const freqLabel = (f) => (FREQUENCIES.find(([k]) => k === f) || [f, f || ''])[1];
const STATUS = { active: 'Active', paused: 'Paused', ended: 'Ended' };
const blankLine = () => ({ description: '', quantity: '1', unit_price: '' });
const field = 'border rounded px-2 py-1';

export function LinesEditor({ lines, onChange, label }) {
  const set = (i, k, v) => onChange(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const total = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
  return (
    <div className="space-y-1" aria-label={label}>
      {lines.map((l, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="flex flex-wrap gap-1">
          <input aria-label={`Line ${i + 1} description`} placeholder="Description" required value={l.description} onChange={(e) => set(i, 'description', e.target.value)} className={`${field} flex-1`} />
          <input aria-label={`Line ${i + 1} quantity`} inputMode="decimal" value={l.quantity} onChange={(e) => set(i, 'quantity', e.target.value)} className={`${field} w-16`} />
          <input aria-label={`Line ${i + 1} price`} inputMode="decimal" placeholder="Price" required value={l.unit_price} onChange={(e) => set(i, 'unit_price', e.target.value)} className={`${field} w-24`} />
          {lines.length > 1 && <button type="button" className="text-red-600 text-xs" onClick={() => onChange(lines.filter((_, j) => j !== i))}>Remove</button>}
        </div>
      ))}
      <div className="flex justify-between text-xs">
        <button type="button" className="text-blue-600" onClick={() => onChange([...lines, blankLine()])}>Add line</button>
        <span data-testid={`${label}-total`}>Total {money(total.toFixed(2))}</span>
      </div>
    </div>
  );
}

function ScheduleForm({ initial, onSave, onCancel, busy }) {
  const [lines, setLines] = useState(initial && initial.lines.length
    ? initial.lines.map((l) => ({ description: l.description, quantity: String(Number(l.quantity)), unit_price: String(l.unit_price) })) : [blankLine()]);
  const [frequency, setFrequency] = useState(initial ? initial.frequency : 'monthly');
  const [start, setStart] = useState(initial ? initial.next_run_date || '' : '');
  const [end, setEnd] = useState(initial ? initial.end_date || '' : '');
  const [dueDays, setDueDays] = useState(initial ? String(initial.due_days) : '0');
  const [description, setDescription] = useState(initial ? initial.description || '' : '');
  const submit = (e) => {
    e.preventDefault();
    const body = { lines, frequency, end_date: end || null, due_days: Number(dueDays) || 0, description };
    if (initial) body.next_run_date = start; else body.start_date = start;
    onSave(body);
  };
  return (
    <form className="border rounded p-2 space-y-2" onSubmit={submit} aria-label={initial ? 'Edit schedule' : 'New schedule'}>
      <input aria-label="Schedule description" placeholder="What this bills for (optional)" value={description} onChange={(e) => setDescription(e.target.value)} className={`${field} w-full`} />
      <LinesEditor lines={lines} onChange={setLines} label="schedule-lines" />
      <div className="flex flex-wrap gap-2 items-center text-xs">
        <label>How often <select aria-label="Frequency" value={frequency} onChange={(e) => setFrequency(e.target.value)} className={field}>
          {FREQUENCIES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        <label>{initial ? 'Next run' : 'First invoice on'} <input type="date" aria-label={initial ? 'Next run date' : 'Start date'} required value={start} onChange={(e) => setStart(e.target.value)} className={field} /></label>
        <label>Ends (optional) <input type="date" aria-label="End date" value={end} onChange={(e) => setEnd(e.target.value)} className={field} /></label>
        <label>Days to pay <input aria-label="Days to pay" inputMode="numeric" value={dueDays} onChange={(e) => setDueDays(e.target.value)} className={`${field} w-14`} /></label>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="border rounded px-3 py-1">{initial ? 'Save changes' : 'Create schedule'}</button>
        <button type="button" onClick={onCancel} className="text-xs underline">Cancel</button>
      </div>
    </form>
  );
}

function InvoiceForm({ onSave, onCancel, busy }) {
  const [lines, setLines] = useState([blankLine()]);
  const [dueDays, setDueDays] = useState('14');
  const [description, setDescription] = useState('');
  const save = (send) => onSave({ lines, due_days: Number(dueDays) || 0, description, send });
  return (
    <form className="border rounded p-2 space-y-2" aria-label="New invoice" onSubmit={(e) => { e.preventDefault(); save(false); }}>
      <input aria-label="Invoice description" placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} className={`${field} w-full`} />
      <LinesEditor lines={lines} onChange={setLines} label="invoice-lines" />
      <label className="text-xs">Days to pay <input aria-label="Invoice days to pay" inputMode="numeric" value={dueDays} onChange={(e) => setDueDays(e.target.value)} className={`${field} w-14`} /></label>
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="border rounded px-3 py-1">Save as draft</button>
        <button type="button" disabled={busy} className="border rounded px-3 py-1" onClick={(e) => { if (e.currentTarget.form.reportValidity()) save(true); }}>Save and send</button>
        <button type="button" onClick={onCancel} className="text-xs underline">Cancel</button>
      </div>
      <p className="text-xs text-gray-600">A draft is not owed yet and the client doesn't see it until you send it.</p>
    </form>
  );
}

export default function ClientBillingPanel({ businessId, client, onChanged }) {
  const [list, setList] = useState([]);
  const [mode, setMode] = useState(null); // 'new' | 'invoice' | schedule id being edited
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setList((await clientPortalAPI.schedules(businessId, client.id)).data.schedules || []); } catch (e) { setError(formatApiError(e, 'Could not load the schedules')); }
  }, [businessId, client.id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, fallback, done) => {
    setBusy(true); setError(''); setNote('');
    try { await fn(); setMode(null); if (done) setNote(done); await load(); if (onChanged) onChanged(); } catch (e) { setError(formatApiError(e, fallback)); }
    setBusy(false);
  };
  const act = (s, action, label) => run(() => clientPortalAPI.scheduleAction(businessId, client.id, s.id, action), `Could not ${label} this schedule`);

  return (
    <div className="border rounded p-2 mt-1 text-sm space-y-2" aria-label={`Billing for ${client.display_name || client.email}`}>
      <div className="flex justify-between items-center">
        <span className="font-medium">Recurring billing</span>
        <span className="flex gap-3 text-xs">
          <button type="button" className="text-blue-600" onClick={() => setMode('new')}>New schedule</button>
          <button type="button" className="text-blue-600" onClick={() => setMode('invoice')}>New invoice</button>
        </span>
      </div>
      {mode === 'new' && <ScheduleForm busy={busy} onCancel={() => setMode(null)}
        onSave={(body) => run(() => clientPortalAPI.createSchedule(businessId, client.id, body), 'Could not create the schedule', 'Schedule created')} />}
      {mode === 'invoice' && <InvoiceForm busy={busy} onCancel={() => setMode(null)}
        onSave={(body) => run(() => clientPortalAPI.createInvoice(businessId, client.id, body), 'Could not save the invoice', body.send ? 'Invoice sent' : 'Draft saved')} />}
      {list.length === 0 ? <p className="text-xs text-gray-600">No recurring schedules yet.</p> : (
        <table className="w-full text-xs" aria-label="Recurring schedules">
          <thead><tr><th className="text-left">Bills</th><th className="text-left">How often</th><th className="text-right">Amount</th><th className="text-left">Next run</th><th className="text-left">Ends</th><th className="text-left">Status</th><th /></tr></thead>
          <tbody>{list.map((s) => (
            <React.Fragment key={s.id}>
              <tr>
                <td>{s.description || (s.lines[0] && s.lines[0].description) || s.invoice_number || `Schedule ${s.id}`}</td>
                <td>{freqLabel(s.frequency)}</td>
                <td className="text-right">{money(s.amount)}</td>
                <td>{s.status === 'active' ? day(s.next_run_date) : '-'}</td>
                <td>{s.end_date ? day(s.end_date) : 'No end date'}</td>
                <td>{STATUS[s.status] || s.status}</td>
                <td className="space-x-2 whitespace-nowrap">
                  {s.status !== 'ended' && <button type="button" className="text-blue-600" onClick={() => setMode(s.id)}>Edit</button>}
                  {s.status === 'active' && <button type="button" disabled={busy} className="text-blue-600" onClick={() => act(s, 'pause', 'pause')}>Pause</button>}
                  {s.status === 'paused' && <button type="button" disabled={busy} className="text-blue-600" onClick={() => act(s, 'resume', 'resume')}>Resume</button>}
                  {s.status !== 'ended' && <button type="button" disabled={busy} className="text-red-600" onClick={() => { if (window.confirm('End this schedule? No more invoices will be created from it.')) act(s, 'end', 'end'); }}>End</button>}
                </td>
              </tr>
              {mode === s.id && <tr><td colSpan={7}><ScheduleForm initial={s} busy={busy} onCancel={() => setMode(null)}
                onSave={(body) => run(() => clientPortalAPI.updateSchedule(businessId, client.id, s.id, body), 'Could not save the schedule', 'Schedule saved')} /></td></tr>}
            </React.Fragment>
          ))}</tbody>
        </table>
      )}
      {note && <p className="text-xs text-green-700" role="status">{note}</p>}
      {error && <p role="alert" className="text-red-600 text-xs">{error}</p>}
    </div>
  );
}
